/* eslint no-magic-numbers: ["error", { "ignore": [0,1,2] }] */
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FormattedMessage } from 'react-intl'
import { saveAs } from 'file-saver'
import qs from 'query-string'

import { usePagination } from '../../../hooks/usePagination'
import sortDirection from '../../../constants/sortDirection'
import {
  downloadIrreplaceabilityReportFile,
  getIrreplaceabilityReportsRequest
} from '../../../api/reports'
import { showErrorToast } from '../../../utils/toast'
import { buildReportsExpandPath } from '../../../routing/constants'
import ExpandedReportsTable from './ExpandedReportsTable'
import {
  makeIrreplaceabilityTabColumns,
  makeIrreplaceabilityTabRow
} from './irreplaceabilityTabTable'
import {
  Dash,
  ReportsPaginationWrapper,
  ReportsTableWrapper,
  ReportTableTitle,
  ReportTableWrapper,
  RightButtons
} from '../style'
import Button from '../../../components/Button'
import Download from '../../../assets/icons/download-icon'
import Expand from '../../../assets/icons/expand-icon'
import Table from '../../../components/Table'
import ItemsPerPageDropdown from '../../../components/ItemsPerPageDropdown'
import Pagination from '../../../features/Pagination'
import { getUrlForAllFilters } from '../index'

const defaultPaginationState = {
  totalNumber: 0,
  resultsPerPage: 10,
  totalPages: 0,
  currentPage: 1,
  hasNext: true,
  hasPrevious: false
}

const defaultFilters = {
  siteFilters: {
    region: [],
    country: [],
    system: [],
    threats: [],
    assessmentStatus: [],
    kbaStatus: [],
    kbaClassification: [],
    kbaCriterias: [],
    dateOfMostRecentAssessment: [],
    url: ''
  },
  speciesFilters: {
    speciesList: [],
    classList: [],
    selectedSpecieOptions: []
  },
  ecosystemFilters: {
    ecosystemList: [],
    ecosystemRedListCategory: [],
    url: ''
  },
  ecologicalFilters: {
    ecoregionList: [],
    url: ''
  },
  showReports: false
}

const IrreplaceabilityReportsTab = ({
  filters,
  setFilters,
  setLoadingScreenMessage,
  isIrreplaceabilityLoading,
  setIsIrreplaceabilityLoading,
  isRunReportTriggered
}) => {
  const history = useNavigate()
  const params = useParams()
  const {
    pagination,
    setPagination,
    selectPage,
    nextPage,
    prevPage,
    nextSection,
    prevSection,
    changeResultsPerPage
  } = usePagination(defaultPaginationState)
  const [irreplaceabilityReports, setIrreplaceabilityReports] = useState([])
  const [irreplaceabilitySorting, setIrreplaceabilitySorting] = useState({
    sortBy: 'siteCode',
    sortDirection: sortDirection.ASC
  })
  const prevRunReportTriggered = useRef(isRunReportTriggered)

  const getIrreplaceabilityReports = async () => {
    if (isIrreplaceabilityLoading) {
      return
    }
    const irreplaceabilityPagination = {
      resultsPerPage: pagination.resultsPerPage,
      currentPage: pagination.currentPage
    }
    const sorting = {
      sortBy: irreplaceabilitySorting.sortBy,
      sortDirection: irreplaceabilitySorting.sortDirection
    }
    setIsIrreplaceabilityLoading(true)

    try {
      const response = await getIrreplaceabilityReportsRequest({
        queryStrings: getUrlForAllFilters(filters),
        pagination: qs.stringify(irreplaceabilityPagination),
        sorting: qs.stringify(sorting)
      })

      if (response.error) {
        setIrreplaceabilityReports([])
        setIsIrreplaceabilityLoading(false)
        setPagination(defaultPaginationState)
        localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
        setFilters(defaultFilters)
        return showErrorToast(response.error, 'showBorder')
      }

      setIrreplaceabilityReports(response.irreplaceability || [])
      setPagination(response.pagination || defaultPaginationState)
      setIsIrreplaceabilityLoading(false)
    } catch (err) {
      setIrreplaceabilityReports([])
      setIsIrreplaceabilityLoading(false)
      setPagination(defaultPaginationState)
      localStorage.setItem('reportsFilters', JSON.stringify(defaultFilters))
      setFilters(defaultFilters)
      return showErrorToast('Something went wrong', 'showBorder')
    }
  }

  const expandTable = () => {
    history(buildReportsExpandPath('irreplaceability'))
  }

  useEffect(() => {
    if (!filters.showReports) {
      return
    }

    const runTriggered = prevRunReportTriggered.current !== isRunReportTriggered
    prevRunReportTriggered.current = isRunReportTriggered

    if (runTriggered && pagination.currentPage !== 1) {
      setPagination((prev) => {
        return { ...prev, currentPage: 1 }
      })
      return
    }

    getIrreplaceabilityReports()
  }, [
    pagination.currentPage,
    pagination.resultsPerPage,
    irreplaceabilitySorting.sortBy,
    irreplaceabilitySorting.sortDirection,
    isRunReportTriggered
  ])

  const downloadCSV = async () => {
    setIsIrreplaceabilityLoading(true)
    setLoadingScreenMessage('buildingReport')
    const queryString = getUrlForAllFilters(filters)

    try {
      const response = await downloadIrreplaceabilityReportFile(queryString)

      if (response.error || !response.blob) {
        showErrorToast('something go wrong')
        setLoadingScreenMessage('runningReport')
        return setIsIrreplaceabilityLoading(false)
      }

      const getDate = new Date().toLocaleDateString('en-GB').replaceAll('/', '_')
      saveAs(response.blob, `Sites_Irreplaceability_report_${getDate}.xlsx`)

      setIsIrreplaceabilityLoading(false)
      setLoadingScreenMessage('runningReport')
    } catch (err) {
      showErrorToast('something go wrong')
      setLoadingScreenMessage('runningReport')
      setIsIrreplaceabilityLoading(false)
    }
  }

  if (params.title && irreplaceabilityReports.length !== 0) {
    return (
      <ExpandedReportsTable
        title={params.title}
        loading={isIrreplaceabilityLoading}
        rows={makeIrreplaceabilityTabRow(irreplaceabilityReports)}
        columns={makeIrreplaceabilityTabColumns(
          irreplaceabilitySorting,
          setIrreplaceabilitySorting,
          irreplaceabilityReports
        )}
        totalNumber={pagination.totalNumber}
        resultsPerPage={pagination.resultsPerPage}
        changeResultsPerPage={changeResultsPerPage}
        download={downloadCSV}
      />
    )
  }

  return (
    <div>
      <ReportTableWrapper>
        <ReportTableTitle>
          <FormattedMessage id='irreplaceability' />
          <Dash>{'—'}</Dash>
          <FormattedMessage id='reports' />
        </ReportTableTitle>
        <RightButtons>
          <Button dataTest='download' height='32px' marginTop='0px' onClick={downloadCSV}>
            <Download />
            <FormattedMessage id='download' />
          </Button>
        </RightButtons>
      </ReportTableWrapper>
      <ReportsTableWrapper activeTab='siteDetails'>
        <Table
          columns={makeIrreplaceabilityTabColumns(
            irreplaceabilitySorting,
            setIrreplaceabilitySorting,
            irreplaceabilityReports
          )}
          rows={makeIrreplaceabilityTabRow(irreplaceabilityReports)}
          noClearFilter={true}
          noResultMessage='noIrreplaceabilityMatchFilteringCriteria'
          dataTest='irreplaceabilityTab-list'
          isLoading={isIrreplaceabilityLoading}
        />
      </ReportsTableWrapper>
      <ReportsPaginationWrapper>
        <ItemsPerPageDropdown
          currentPage={pagination.currentPage}
          resultsPerPage={pagination.resultsPerPage}
          totalNumber={pagination.totalNumber}
          onChange={changeResultsPerPage}
          dataTest='irreplaceability-items-per-page'
          menuPlacement='top'
          alternativeOptions={true}
        />
        {pagination.totalPages > 1 && (
          <Pagination
            activePages={pagination.currentPage}
            numberOfPages={pagination.totalPages}
            nextPage={nextPage}
            prevPage={prevPage}
            nextSection={nextSection}
            prevSection={prevSection}
            selectPage={selectPage}
            dataTest='page-number'
          />
        )}
      </ReportsPaginationWrapper>
    </div>
  )
}

export default IrreplaceabilityReportsTab
